/**
 * 이상한 방 - 문 앞 줄 (원래 방 버튼 옆의 칩 줄: 보유자 · 줄 선 사람 · 내 차례 유예, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.4절(원래 방 - 정원 1, 체류 상한) · 5절(웃김 설계 포인트 - "빨리 나와!!")
 *            `이상한방-상태모델-v0.1.md` 11.3절(입장 허가 · 타이머는 서버가 쥔다) · 원칙 2(UI가 덮은 면적은 곧 볼 수 없는 방의 넓이)
 *            로드맵 M5(`doorQueue` - 보유자 이름 + 남은 체류 시간이 전원에게("OO 나가기 7"), 줄 선 사람 마커, 내 차례 유예 게이지 · 규칙 5)
 *
 * ## 문 하나를 전원이 본다
 *
 * 원래 방은 정원 1이다(4.4절). 누가 들어가 있고 몇 초 뒤에 나오는지가 **전원에게** 보여야 "빨리 나와!!"가 되고(5절), 그것이 이 줄의
 * 존재 이유다. 보유자 칩("OO 나가기 7")은 보유자 본인에게도 남에게도 같은 값이고, 줄 선 사람은 이름 마커로, 차례가 온 사람에게는
 * 유예 게이지가 돈다. 문 앞에 아무도 없으면 줄째 없다 - 빈 문을 알리는 칩은 정보가 아니다.
 *
 * ## 남은 시간을 여기서 세지 않는다 (M5 규칙 5)
 *
 * 보유자의 남은 체류와 예약 유예 잔여는 호스트가 `DoorView`에 내린 값(`matchView.doorView`)이고, `step` 이후 흐른 만큼 빼는 것은
 * 시계 보간(`matchClock.doorAfter`)이다. 이 파일은 받은 ms를 **초로 올려 적는 것**(`Math.ceil`)과 게이지의 비율만 한다 - 그것은
 * 표시이고 규칙이 아니다(`gameHud.describeOrigin`이 원래 방 버튼에 하는 것과 같은 선). 여기서 체류 상한이나 유예 길이를 읽어 남은
 * 시간을 만들기 시작하면 호스트의 문과 화면의 문이 갈리는 날이 온다. 테스트가 그 선을 지킨다 - 뷰의 ms를 바꾸면 표시만 바뀐다.
 *
 * ## 예약자는 줄 머리다
 *
 * 문이 비면 리듀서는 줄 머리를 그대로 두고 `reservedFor`로 세운다(`matchOrigin.reserveNext` - 들어가거나 유예를 넘길 때 줄에서 빠진다).
 * 그래서 예약자가 있으면 머리 마커 자리에 "차례" 칩이 선다 - 같은 사람을 마커와 칩으로 두 번 세우지 않기 위한 **배치 규약**이고,
 * 누가 예약자인지는 여전히 뷰의 `reservedName`이다.
 *
 * ## 자리
 *
 * 원래 방 버튼(`.gh-origin`, 오른쪽 아래) 바로 왼쪽, 같은 바닥 줄. 버튼과 칩이 한 줄에 서야 "그 문의 줄"로 읽힌다. 관전 중에는
 * 그 자리를 시점 줄(`spectateBar.ts`)이 쓰므로 왼쪽 아래 탄환 블록 위로 비킨다(`aside`). 누르는 것이 없다 - `pointer-events: none`.
 * 색은 전부 `--gh-*`.
 */

import { ceilSeconds } from '../core/time'
import { t } from '../i18n'

import { createElement, setAttr, setBarRatio, setFlag, setHidden, setText } from './dom'
import { publishSize } from './overlayLayout'

import './doorQueue.css'

/* --- 타입 ---------------------------------------------------------------------- */

/** 이 줄이 그리는 데 필요한 전부. 시간 둘은 보간까지 끝난 ms다(`matchSessionView.buildDoorQueueView`). */
export type DoorQueueView = Readonly<{
  holderName: string | null
  holderIsMe: boolean
  /** 보유자의 남은 체류(ms). 보유자가 없으면 0. */
  holderRemainingMs: number
  /** 줄 선 사람들의 이름, 머리부터. */
  queue: readonly string[]
  /** 내 줄 자리(1부터). 줄에 없으면 `null`. */
  myQueuePosition: number | null
  reservedName: string | null
  reservedIsMe: boolean
  /** 예약 유예 잔여(ms). 예약자가 없으면 0. */
  reservedRemainingMs: number
  /** 유예 게이지의 분모(ms). 뷰에 없어 세션이 튜닝(`doorReserveSeconds`)에서 넘긴다. */
  reserveTotalMs: number
  /** 내가 관전 중인가. 시점 줄이 원래 방 버튼 자리를 쓰므로 이 줄은 옆으로 비킨다(머리말 "자리"). */
  aside: boolean
}>

export type DoorQueueHandle = Readonly<{
  root: HTMLElement
  render: (view: DoorQueueView) => void
  /** 배치 모드·잠든 무대에서는 줄째 접는다(HUD의 `setVisible`과 같은 시점). */
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

export type DoorQueueOptions = Readonly<{ mount: HTMLElement }>

/* --- 순수 조각 ------------------------------------------------------------------ */

/** ms → 화면의 초. 올림이다 - 0.2초 남았을 때 "0"은 이미 나온 사람으로 읽힌다. `core/time.ceilSeconds` 그대로(HUD와 같은 함수). */
export { ceilSeconds }

/** 문 앞에 보여 줄 것이 하나라도 있는가. 없으면 줄째 숨는다. */
export function hasDoorActivity(view: DoorQueueView): boolean {
  return view.holderName !== null || view.queue.length > 0 || view.reservedName !== null
}

/* --- 본체 ---------------------------------------------------------------------- */

export function createDoorQueue(options: DoorQueueOptions): DoorQueueHandle {
  const root = createElement('div', 'dq-root')
  root.setAttribute('role', 'status')
  root.hidden = true

  const holder = createElement('span', 'dq-chip dq-holder', '')
  const reserved = createElement('span', 'dq-chip dq-reserved')
  const reservedText = createElement('span', 'dq-reserved__text', '')
  const gauge = createElement('span', 'gh-bar dq-gauge')
  gauge.setAttribute('aria-hidden', 'true')
  const gaugeFill = createElement('i', 'gh-bar__fill')
  gauge.append(gaugeFill)
  reserved.append(reservedText, gauge)
  const queue = createElement('ol', 'dq-queue')
  root.append(queue, reserved, holder)
  options.mount.append(root)

  /*
   * 자기 높이를 내보낸다. 폰 세로에서 이 줄은 왼쪽 아래, **카메라 묶음 위**에 서므로(`doorQueue.css`의 미디어 쿼리)
   * 위 기둥이 그만큼 더 일찍 멈춰야 한다 - 안 그러면 참가자 줄의 잘린 끝이 이 줄 위로 내려앉는다.
   * 접혀 있을 때는 0이라(`[hidden]`이면 사각형이 0이다) 평소에는 아무것도 움직이지 않는다.
   */
  const size = publishSize(root, { height: '--dq-height' })

  /** 줄 마커. 이름 목록이 달라질 때만 다시 세운다(프레임마다 불리는 경로다). */
  let markers: HTMLLIElement[] = []
  let names: readonly string[] = []

  function sameNames(next: readonly string[]): boolean {
    return next.length === names.length && next.every((name, index) => name === names[index])
  }

  function rebuildMarkers(next: readonly string[]): void {
    names = next
    markers = next.map(() => createElement('li', 'dq-marker', ''))
    queue.replaceChildren(...markers)
  }

  function paintQueue(view: DoorQueueView): void {
    if (!sameNames(view.queue)) rebuildMarkers(view.queue)
    setHidden(queue, view.queue.length === 0)
    setAttr(queue, 'aria-label', t('door.queueAria', { count: view.queue.length }))
    view.queue.forEach((name, index) => {
      const marker = markers[index]
      if (marker === undefined) return
      const position = index + 1
      setText(marker, name)
      setAttr(marker, 'aria-label', t('door.queuedAria', { name, position }))
      setFlag(marker, 'is-me', view.myQueuePosition === position)
      // 머리는 예약자가 있으면 "차례" 칩으로 선다(머리말 "예약자는 줄 머리다").
      setHidden(marker, index === 0 && view.reservedName !== null)
    })
  }

  return {
    root,

    render(view: DoorQueueView): void {
      setHidden(root, !hasDoorActivity(view))
      setFlag(root, 'is-aside', view.aside)
      setAttr(root, 'aria-label', t('door.aria'))

      setHidden(holder, view.holderName === null)
      if (view.holderName !== null) {
        // 내가 보유자면 문장을 간다 - '나 나가기 7'은 이름 자리에 '나'를 넣은 문장이라 읽히지 않는다(리빌 이름표의 'mine' 얼굴과 같은 결정).
        setText(
          holder,
          view.holderIsMe
            ? t('door.holderMe', { seconds: ceilSeconds(view.holderRemainingMs) })
            : t('door.holder', { name: view.holderName, seconds: ceilSeconds(view.holderRemainingMs) }),
        )
        setFlag(holder, 'is-me', view.holderIsMe)
      }

      setHidden(reserved, view.reservedName === null)
      if (view.reservedName !== null) {
        setFlag(reserved, 'is-me', view.reservedIsMe)
        setText(
          reservedText,
          view.reservedIsMe
            ? t('door.yourTurn', { seconds: ceilSeconds(view.reservedRemainingMs) })
            : t('door.reservedFor', { name: view.reservedName }),
        )
        // 게이지는 내 차례에만 돈다 - 남의 유예가 줄어드는 것은 "곧 다음 사람"이라는 정보보다 시선을 더 끈다.
        setHidden(gauge, !view.reservedIsMe)
        if (view.reservedIsMe) {
          setBarRatio(gaugeFill, view.reserveTotalMs > 0 ? view.reservedRemainingMs / view.reserveTotalMs : 0)
        }
      }

      paintQueue(view)
      // 접었다 폈다는 관측기가 놓칠 수 있는 순간이다(배경 탭에서는 콜백이 아예 안 온다).
      size.refresh()
    },

    setVisible(visible: boolean): void {
      // 접을 때만 손댄다. 펼 때는 다음 `render`가 문 앞의 활동으로 다시 정한다.
      if (!visible) setHidden(root, true)
      size.refresh()
    },

    dispose(): void {
      size.dispose()
      root.remove()
    },
  }
}
